import React, { createContext, useReducer } from "react";
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AnalyticsService } from "../../constants/Analytics";
import Reducer from './Reducer';
import config from '../../constants/Config';

import BrandService from "../../services/BrandService";
import ConfigService from "../../services/ConfigService";
import ManufacturerService from "../../services/ManufacturerService";
import ProductService from "../../services/ProductService";
import { SafeJSONObject } from '../../constants/Utils';
import useMounted from '../../hooks/useMounted';
import { IProductContextState, IProductContextValue, ProductContextActions, ProductContextActionTypes } from '../../interfaces/product';

const analytics = new AnalyticsService();
const productService = new ProductService();
const brandService = new BrandService();
const manufacturerService = new ManufacturerService();
const configService = new ConfigService();

const initialState: IProductContextState = {
    loading: false,
    query: '',
    products: [],
    all_products: [],
    brands: [],
    // brands_dict: {},
    manufacturers: [],
    // route_fulfilments: {},
    active_brand: null,
    active_manufacturer: null,

    categories: [],

    search_context: null,
    search_result: [],
    search_result_exact: [],
    search_result_match: [],
    product_view: 'list',
    product_view_default: 'list',
    seller_product: [],
    seller_product_dict: {},
    error: null,
    
    product_page: 0,
    product_page_total: 0,
    product_page_count: 0,
    loading_next_product: false,
    
    brand_page: 0,
    brand_page_total: 0,
    brand_page_count: 0,
    loading_next_brand: false,
    
    manufacturer_page: 0,
    manufacturer_page_total: 0,
    manufacturer_page_count: 0,
    loading_next_manufacturer: false,
    
    category_page: 0,
    category_page_total: 0,
    category_page_count: 0,
    loading_next_category: false,
};

const ProductStore = ({ ...props }) => {
    const isMounted = useMounted();
    const [state, dispatch] = useReducer(Reducer, initialState);
    
    const fetch = () => {
        return new Promise(async (resolve, reject) => {
            try {
                isMounted.current && dispatch({ type: ProductContextActionTypes.SET_QUERY, payload: '' })
                isMounted.current && dispatch({ type: ProductContextActionTypes.SET_LOADING, payload: false })

                let buyer = SafeJSONObject(await AsyncStorage.getItem('buyerData'))
                let product_view_default = await AsyncStorage.getItem('productViewUser')

                let page = 1

                let req_paged_products = productService.fetchAvailable(buyer.location._id, page, config.pagination_limit)
                // let req_all_products = productService.fetchAllAvailable(buyer.location._id)
                let req_all_brands = brandService.fetchAllAvailable(buyer.location._id);
                let req_manufacturer = manufacturerService.fetchAllAvailable(buyer.location._id);
                let req_top_manufacturer = manufacturerService.topByBuyer(buyer._id);
                let req_product_view = configService.fetchByKey('PRODUCT_VIEW');

                Promise.all([
                    req_manufacturer,
                    req_top_manufacturer,
                    req_all_brands,
                    req_paged_products,
                    // req_all_products,
                    req_product_view
                ])
                    .then(response => {
                        let [
                            res_manufacturer,
                            res_top_manufacturer,
                            res_brands,
                            res_paged_products,
                            // res_all_products,
                            res_product_view
                        ] = response

                        let product_view = res_product_view[0]?.value

                        // all dispatch should happen when context component is mounted
                        if (isMounted.current) {
                            dispatch({
                                type: ProductContextActionTypes.SET_PAGED_PRODUCTS,
                                payload: res_paged_products
                            })
                            // dispatch({
                            //     type: 'SET_ALL_PRODUCTS',
                            //     payload: res_all_products
                            // })
                            dispatch({
                                type: ProductContextActionTypes.SET_PRODUCT_VIEW,
                                payload: product_view_default
                                    ? product_view_default
                                    : product_view
                                        ? product_view
                                        : 'list'
                            })

                            dispatch({
                                type: ProductContextActionTypes.SET_BRANDS,
                                payload: res_brands
                            })

                            dispatch({
                                type: ProductContextActionTypes.SET_TOP_MANUFACTURERS,
                                payload: res_top_manufacturer
                            })
                            dispatch({
                                type: ProductContextActionTypes.SET_MANUFACTURERS,
                                payload: res_manufacturer
                            })
                        }
                        resolve(null)

                    }).catch(e => {
                        reject({message: e.message})
                    })
            } catch (e) {
                reject({message: e.message})
            }

        })
    }

    const fetchNextProduct = () => {
        return new Promise(async (resolve, reject) => {
            try {
                isMounted.current && dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_PRODUCT, payload: true })

                let buyer = SafeJSONObject(await AsyncStorage.getItem('buyerData'))

                let page = state.product_page + 1

                productService.fetchAvailable(buyer.location._id, page, config.pagination_limit)
                    .then(response => {
                        if (isMounted.current) {
                            dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_PRODUCT, payload: false })
                            dispatch({
                                type: ProductContextActionTypes.SET_PAGED_PRODUCTS,
                                payload: response
                            })
                        }
                        resolve(null)
                    })
                    .catch(e => {
                        dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_PRODUCT, payload: false })
                        reject({message: e.message})
                        throw e
                    })
            } catch (e) {
                dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_PRODUCT, payload: false })
                reject({message: e.message})
                throw e
            }

        })
    }

    const fetchNextManufacturer = () => {
        return new Promise(async (resolve, reject) => {
            try {
                if(state.loading_next_manufacturer) {
                    reject({message: 'Loading maunfacturer next page'})
                }
                if(state.manufacturer_page >= state.manufacturer_page_total) {
                    reject({message: 'Maximum manufacturer page reached'})
                }
                dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_MANUFACTURER, payload: true })

                let buyer = SafeJSONObject(await AsyncStorage.getItem('buyerData'))

                let page = state.manufacturer_page + 1

                productService.fetchAvailableManufacturers(buyer.location._id, page, config.pagination_limit)
                    .then(response => {
                        if (isMounted.current) {
                            dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_MANUFACTURER, payload: false })
                            dispatch({
                                type: ProductContextActionTypes.SET_PAGED_MANUFACTURERS,
                                payload: response
                            })
                        }
                        resolve(null)
                    })
                    .catch(e => {
                        dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_MANUFACTURER, payload: false })
                        reject({message: e.message})
                        throw e
                    })
            } catch (e) {
                dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_MANUFACTURER, payload: false })
                reject({message: e.message})
                throw e
            }

        })
    }

    const fetchNextCategory = () => {
        return new Promise(async (resolve, reject) => {
            try {
                if(state.loading_next_category) {
                    reject({message: 'Loading category next page'})
                }
                if(state.category_page >= state.category_page_total) {
                    reject({message: 'Maximum category page reached'})
                }
                dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_CATEGORY, payload: true })

                let buyer = SafeJSONObject(await AsyncStorage.getItem('buyerData'))
                try {
                    buyer = JSON.parse(buyer)
                } catch (e) {
                    buyer = {}
                }

                let page = state.category_page + 1

                productService.fetchAvailableCategories(buyer.location._id, page, config.pagination_limit)
                    .then(response => {
                        if (isMounted.current) {
                            dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_CATEGORY, payload: false })
                            dispatch({
                                type: ProductContextActionTypes.SET_PAGED_CATEGORIES,
                                payload: response
                            })
                        }
                        resolve(null)
                    })
                    .catch(e => {
                        dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_CATEGORY, payload: false })
                        reject({message: e.message})
                        throw e
                    })
            } catch (e) {
                dispatch({ type: ProductContextActionTypes.SET_LOADING_NEXT_CATEGORY, payload: false })
                reject({message: e.message})
                throw e
            }

        })
    }

    return (
        <ProductContext.Provider
            value={{
                product_state: state,
                product_dispatch: dispatch,
                product_fetch: fetch,
                fetch_next_product: fetchNextProduct,
                fetch_next_manufacturer: fetchNextManufacturer,
                fetch_next_category: fetchNextCategory
            }}
        >
            {props.children}
        </ProductContext.Provider>
    )
};

export const ProductContext = createContext<IProductContextValue>({
    product_state: initialState,
    product_dispatch: (arg: any) => null,
    product_fetch: () => null,
    fetch_next_product: () => null,
    fetch_next_manufacturer: () => null,
    fetch_next_category: () => null
});
export default ProductStore;
